import type { DuplicateRecord, Transaction } from '@/modules/projects'
import {
  compareByPrintedOrder,
  printedPositionLabel,
} from '@/modules/projects/components/reconciliation/helpers'
import { getProjectPath } from '@/utils'

export const duplicatesPath = (slug: string) =>
  `${getProjectPath({ slug })}/insights/duplicates`

// The group id is an opaque server string, so encode it rather than trusting it to be path-safe.
export const duplicateReviewPath = (slug: string, groupId: string) =>
  `${duplicatesPath(slug)}/${encodeURIComponent(groupId)}`

export interface DuplicateGroup {
  // The server's duplicate_group_id. Stable enough to address one group in a URL.
  id: string
  date: string
  amount: number
  description: string
  transactions: Transaction[]
}

function indexTransactionsById(
  transactions: Transaction[],
): Map<number, Transaction> {
  const indexed = new Map<number, Transaction>()
  for (const transaction of transactions) {
    indexed.set(transaction.id, transaction)
  }
  return indexed
}

function bucketDuplicatesByGroupId(
  duplicates: DuplicateRecord[],
): Map<string, DuplicateRecord[]> {
  const byGroupId = new Map<string, DuplicateRecord[]>()
  for (const duplicate of duplicates) {
    const groupId = duplicate.duplicateGroupId
    if (!groupId) continue
    const existing = byGroupId.get(groupId) ?? []
    existing.push(duplicate)
    byGroupId.set(groupId, existing)
  }
  return byGroupId
}

export function groupDuplicates(
  duplicates: DuplicateRecord[],
  transactions: Transaction[],
): DuplicateGroup[] {
  const transactionsById = indexTransactionsById(transactions)
  const duplicatesByGroupId = bucketDuplicatesByGroupId(duplicates)

  const groups: DuplicateGroup[] = []
  // Map iteration keeps the server's order, which the review pager reports as "3 of 12".
  for (const [groupId, members] of duplicatesByGroupId) {
    if (members.length < 2) continue

    const fullTransactions: Transaction[] = []
    for (const member of members) {
      const transaction = transactionsById.get(member.id)
      if (transaction) fullTransactions.push(transaction)
    }
    if (fullTransactions.length < 2) continue

    groups.push({
      id: groupId,
      date: members[0].date,
      amount: members[0].amount,
      description: members[0].description,
      transactions: fullTransactions,
    })
  }
  return groups
}

// Where a duplicate row printed. The reconciliation label needs a row position as well as a page,
// and duplicate rows are extracted without one, so fall back to naming the page on its own.
export const memberPageLabel = (row: Transaction): string | null =>
  printedPositionLabel(row) ??
  (row.sourcePage != null ? `p.${row.sourcePage}` : null)

// The page every copy printed on, or null when they are spread over more than one. Copies of one
// charge usually share a page, and saying so explains why picking a row does not move the document.
export const sharedMemberPage = (rows: Transaction[]): number | null => {
  const pages = rows.map((row) => row.sourcePage)
  if (pages.length === 0 || pages.some((page) => page == null)) return null
  return new Set(pages).size === 1 ? (pages[0] as number) : null
}

// One span covering every copy, plus a page either side for context. Copies almost always share a
// page, so the span is normally tiny; copies at opposite ends of an upload crop nothing.
const PAGE_CONTEXT = 1

export const widenPageRange = (
  range: { start: number; end: number } | null,
  page: number | null,
): { start: number; end: number } | null => {
  if (page == null) return range
  const start = Math.max(1, page - PAGE_CONTEXT)
  const end = page + PAGE_CONTEXT
  if (!range) return { start, end }
  return { start: Math.min(range.start, start), end: Math.max(range.end, end) }
}

// Members of one group can sit in different statements; keep each statement's rows together, in the
// order they print, so the reviewer walks one document at a time.
export const compareDuplicateMembers = (
  first: Transaction,
  second: Transaction,
) =>
  first.statementId - second.statementId || compareByPrintedOrder(first, second)
